<template>
  <unit-page-skeleton v-if="!unitTypeComponent" />
  <component :is="unitTypeComponent" v-else />
</template>

<script setup lang="ts">
import { unitPageStore, type UpdatedUnitResponseDTO } from "~/store/unitPage";
import { gtagReachGoal } from "~/services/SEO/goals/createCustomGoal";
import { Api } from "~/services/swagger/Api";
import UnitPageEstateUnit from "~/components/unit-page/estate-unit/index.vue";
import UnitPageLandUnit from "~/components/unit-page/land-unit/index.vue";

const UNIT_TYPE_ESTATE = "Estate";
const UNIT_TYPE_LAND = "Land";

definePageMeta({
  layout: "default",
  middleware: ["auth-middleware", "unit-page-middleware"],
});

const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const useUnitpageStore = unitPageStore();

const id = computed(() => route.params.id as string);

// Skip blocking SSR — the unit fetch happens on the client so the server
// can flush HTML (skeleton) instantly and the user sees the layout right away.
const { data: unit, error } = useAsyncData(
  `unit-${id.value}`,
  async () => {
    if (!id.value) return null;
    const api = new Api();
    const response = await api.api.unitsControllerRetrieve(id.value);
    return (response?.data as UpdatedUnitResponseDTO) || null;
  },
  { lazy: true, server: false, watch: [id] }
);

// Bind the resolved component object — not a string. Auto-imported components
// are registered locally (compile-time), so a runtime string passed to
// `<component :is>` would not resolve and the page would render blank.
const unitTypeComponent = computed(() => {
  const data = unit.value;
  if (data?.type === UNIT_TYPE_ESTATE) return UnitPageEstateUnit;
  if (data?.type === UNIT_TYPE_LAND) return UnitPageLandUnit;
  return null;
});

watch(
  unit,
  (newData) => {
    if (!newData) return;
    useUnitpageStore.proccessUnitPageData(newData);
    useUnitpageStore.$patch({
      currentElementInstance: newData.type || "",
    });
    gtagReachGoal.visit__unit({ value: newData.name });

    if (id.value === newData.id && newData.sanitizeName) {
      router.replace(localePath(`/property/${newData.sanitizeName}`));
    }
  },
  { immediate: true }
);

watch(
  error,
  (err) => {
    if (err) {
      showError({ statusCode: 404, statusMessage: "Property not found" });
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss"></style>
